import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface ProfileFilters {
  search: string;
  gender?: "male" | "female";
  ageMin?: number;
  ageMax?: number;
  locations: string[];
  religiosityLevels: string[];
  educationLevels: string[];
  ethnicities: string[];
  maritalStatuses: string[];
  citizenshipStatuses: string[];
  sects: string[];
  activeOnly: boolean;
  status?: "pending" | "active" | "declined" | "all";
}

interface Profile {
  id: string;
  first_name: string;
  age: number;
  gender: string;
  location: string;
  education_level: string;
  religiosity_level: string;
  prayer_frequency: string;
  bio?: string;
  profile_picture_url?: string;
  is_active: boolean;
  created_at: string;
  status: string | null;
  invite_email: string | null;
  claim_token: string | null;
  claimed_at: string | null;
  created_by: string | null;
}

const ITEMS_PER_PAGE = 20;

export function useMatchmakerProfiles(filters: ProfileFilters, page: number = 1) {
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchProfiles = async () => {
      try {
        setLoading(true);
        setError(null);

        let query = supabase
          .from("profiles")
          .select("*", { count: "exact" });

        // Hide junk rows auto-created by the signup trigger before the user
        // has actually completed /profile/create. Matchmaker-created stubs
        // (created_by not null) remain visible for claim tracking.
        query = query.or("profile_completed.eq.true,created_by.not.is.null");

        // Apply filters
        if (filters.search) {
          query = query.ilike("first_name", `%${filters.search}%`);
        }

        if (filters.gender) {
          query = query.eq("gender", filters.gender);
        }

        if (filters.ageMin) {
          query = query.gte("age", filters.ageMin);
        }

        if (filters.ageMax) {
          query = query.lte("age", filters.ageMax);
        }

        if (filters.locations.length > 0) {
          query = query.in("location", filters.locations);
        }

        if (filters.religiosityLevels.length > 0) {
          query = query.in("religiosity_level", filters.religiosityLevels);
        }

        if (filters.educationLevels.length > 0) {
          query = query.in("education_level", filters.educationLevels);
        }

        if (filters.ethnicities.length > 0) {
          query = query.in("ethnicity", filters.ethnicities);
        }

        if (filters.maritalStatuses.length > 0) {
          query = query.in("marital_status", filters.maritalStatuses);
        }

        if (filters.citizenshipStatuses.length > 0) {
          query = query.in("citizenship_status", filters.citizenshipStatuses);
        }

        if (filters.sects.length > 0) {
          query = query.in("sect", filters.sects);
        }

        if (filters.activeOnly) {
          query = query.eq("is_active", true);
        }

        // Status filter
        if (filters.status && filters.status !== "all") {
          query = query.eq("status", filters.status);
        }

        // Pagination
        const from = (page - 1) * ITEMS_PER_PAGE;
        const to = from + ITEMS_PER_PAGE - 1;
        query = query.range(from, to);

        // Execute query
        const { data, error: queryError, count } = await query.order("created_at", { ascending: false });

        if (queryError) throw queryError;

        setProfiles(data || []);
        setTotalCount(count || 0);
      } catch (err: any) {
        console.error("matchmaker.fetch_profiles_failed");
        setError(err.message);
        toast.error("Failed to load profiles");
      } finally {
        setLoading(false);
      }
    };

    fetchProfiles();
  }, [filters, page]);

  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);

  return {
    profiles,
    loading,
    error,
    totalCount,
    totalPages,
    currentPage: page,
  };
}
